/**
 * Detalle de producto con tabs: General, Stock, Seriales, Precios, Movimientos.
 * Carga perezosa de cada tab.
 */
import { useState } from 'react';
import { Link, useNavigate, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Edit, Trash2, ExternalLink } from 'lucide-react';

import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Can } from '@/components/permissions/Can';
import { PERMISSIONS } from '@/permissions/constants';
import { formatCost, formatMoney } from '@/lib/money';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

import { useProduct, useProductSerials, useProductStockByWarehouse, useProductMovements, useProductImages } from '@/features/inventory-center/api';
import { PricesEditor } from '@/features/inventory-center/components/PricesEditor';
import { ProductRelations } from '@/features/inventory-center/components/ProductRelations';
import { ProductVariantsTab } from '@/features/inventory-center/components/ProductVariantsTab';
import { ProfitMarginPanel } from '@/features/inventory-center/components/ProfitMarginPanel';
import { KardexTab } from '@/features/inventory-center/components/KardexTab';
import { AuditsTab } from '@/features/inventory-center/components/AuditsTab';
import { ProductGalleryPanel } from '@/features/inventory-center/components/ProductGalleryPanel';
import { ImageGallery } from '@/features/inventory-center/components/ImageGallery';
import { movementTypeLabel, MOVEMENT_IN_TYPES, referenceTypeLabel, referenceLink } from '@/features/inventory-center/movementLabels';
import { EditProductDialog } from '@/features/inventory-center/dialogs/EditProductDialog';
import { DeleteProductDialog } from '@/features/inventory-center/dialogs/DeleteProductDialog';
import type { ProductStock, ProductSerial, ProductMovement } from '@/features/inventory-center/schemas';

export const Route = createFileRoute('/_authed/inventory/$productId')({
  component: ProductDetailPage,
});

function ProductDetailPage() {
  const { productId } = Route.useParams();
  const id = parseInt(productId, 10);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Hooks (siempre antes de cualquier return condicional).
  const { data: product, isLoading, isError } = useProduct(id);
  const { data: stock_by_warehouse = [] } = useProductStockByWarehouse(id);
  const { data: serialsData = [] } = useProductSerials(id);
  const { data: recent_movements = [] } = useProductMovements(id);
  const { data: galleryImages = [] } = useProductImages(id);
  const serials: ProductSerial[] = serialsData;

  if (isLoading) {
    return (
      <PageLayout title="Cargando producto...">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </PageLayout>
    );
  }

  if (isError || !product) {
    return (
      <PageLayout title="Producto no encontrado">
        <EmptyState
          title="No se encontró el producto"
          description="El producto puede haber sido eliminado o no tienes permiso para verlo."
          action={
            <Button
              variant="outline"
              onClick={() =>
                navigate({
                  to: '/inventory',
                  search: {
                    search: '',
                    tracking: 'all',
                    stock: 'all',
                    status: 'all',
                    page: 1,
                    brand_id: undefined,
                    category_id: undefined,
                    tag_id: undefined,
                    warehouse_id: undefined,
                    low_stock_threshold: undefined,
                    sort_by: undefined,
                    sort_dir: undefined,
                  },
                })
              }
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Volver al inventario
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={product.name}
      description={`SKU ${product.sku} · ${product.tracking_type === 'serialized' ? 'Serializado' : 'Por cantidad'}`}
      breadcrumb={
        <Link
          to="/inventory"
          search={{
            search: '',
            tracking: 'all',
            stock: 'all',
            status: 'all',
            page: 1,
            brand_id: undefined,
            category_id: undefined,
            tag_id: undefined,
            warehouse_id: undefined,
            low_stock_threshold: undefined,
            sort_by: undefined,
            sort_dir: undefined,
          }}
          className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-primary"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Inventario
        </Link>
      }
      actions={
        <div className="flex items-center gap-2">
          <Can I={PERMISSIONS.PRODUCTS_UPDATE}>
            <Button
              variant="outline"
              leftIcon={<Edit className="size-4" />}
              onClick={() => setEditOpen(true)}
              data-testid="edit-product"
            >
              Editar
            </Button>
          </Can>
          <Can I={PERMISSIONS.PRODUCTS_DELETE}>
            <Button
              variant="danger"
              leftIcon={<Trash2 className="size-4" />}
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-product"
            >
              Eliminar
            </Button>
          </Can>
        </div>
      }
    >
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="stock">Stock</TabsTrigger>
          {product.tracking_type === 'serialized' && (
            <TabsTrigger value="serials">Seriales / IMEI</TabsTrigger>
          )}
          <TabsTrigger value="prices">Precios por lista</TabsTrigger>
          <TabsTrigger value="images">Galería</TabsTrigger>
          <Can I={PERMISSIONS.PRODUCTS_UPDATE}>
            <TabsTrigger value="variants">Variantes</TabsTrigger>
          </Can>
          <TabsTrigger value="movements">Movimientos</TabsTrigger>
          <TabsTrigger value="kardex">Kardex</TabsTrigger>
          <TabsTrigger value="audits">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Galeria de imagenes a la izquierda (col 1) */}
            <div className="lg:col-span-1">
              <ProductGalleryPanel
                images={product.images ?? galleryImages}
                fallbackUrl={product.image_url}
              />
            </div>

            {/* Datos y relaciones a la derecha (cols 2-3) */}
            <div className="space-y-4 lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Información general</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="SKU"><code className="rounded bg-bg px-1.5 py-0.5 text-xs">{product.sku}</code></Field>
                  <Field label="Nombre">{product.name}</Field>
                  <Field label="Tipo">
                    <Badge variant={product.tracking_type === 'serialized' ? 'info' : 'default'}>
                      {product.tracking_type === 'serialized' ? 'Serializado (IMEI/serial)' : 'Por cantidad'}
                    </Badge>
                  </Field>
                  <Field label="Estado">
                    <Badge variant={product.is_active ? 'success' : 'default'}>
                      {product.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </Field>
                  <Field label="Precio base">{formatMoney(product.base_price)}</Field>
                  <Field label="Precio de costo">
                    {product.last_purchase_cost != null ? formatMoney(product.last_purchase_cost) : (product.average_cost != null ? formatMoney(product.average_cost) : '—')}
                  </Field>
                  <Field label="Margen">
                    {product.profit_margin != null ? (
                      <span className="font-semibold text-primary">{Number(product.profit_margin).toFixed(1)}%</span>
                    ) : (
                      <span className="text-text-muted">Sin margen</span>
                    )}
                  </Field>
                  <Field label="Moneda de venta preferida">{product.sale_currency ?? '—'}</Field>
                  <Field label="Vendible">
                    <Badge variant={product.is_active ? 'success' : 'default'}>
                      {product.is_active ? 'Sí' : 'No'}
                    </Badge>
                  </Field>
                  <Field label="Tiene garantía">
                    <Badge variant={product.warranty_policy_id ? 'info' : 'default'}>
                      {product.warranty_policy_id ? 'Sí' : 'No'}
                    </Badge>
                  </Field>
                  <Field label="Última actualización">
                    {product.updated_at ? formatRelative(product.updated_at) : '—'}
                  </Field>
                </CardContent>
              </Card>

              <ProductRelations product={product} />
              <ProfitMarginPanel product={product} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="stock" className="space-y-4">
          <StockTab productId={id} initialStock={stock_by_warehouse} />
        </TabsContent>

        {product.tracking_type === 'serialized' && (
          <TabsContent value="serials" className="space-y-4">
            <SerialsTab productId={id} initialSerials={serials} />
          </TabsContent>
        )}

        <TabsContent value="prices" className="space-y-4">
          <PricesTab productId={id} />
        </TabsContent>

        <TabsContent value="images" className="space-y-4">
          <ImageGallery productId={id} images={galleryImages} canEdit />
        </TabsContent>


        <Can I={PERMISSIONS.PRODUCTS_UPDATE}>
          <TabsContent value="variants" className="space-y-4">
            <ProductVariantsTab productId={id} />
          </TabsContent>
        </Can>

        <TabsContent value="movements" className="space-y-4">
          <MovementsTab movements={recent_movements} />
        </TabsContent>

        <TabsContent value="kardex" className="space-y-4">
          <KardexTab productId={id} />
        </TabsContent>

        <TabsContent value="audits" className="space-y-4">
          <AuditsTab productId={id} />
        </TabsContent>
      </Tabs>

      {product && (
        <EditProductDialog
          product={product}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}

      {product && (
        <DeleteProductDialog
          productId={product.id}
          productName={product.name}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />
      )}
    </PageLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function StockTab({
  productId,
  initialStock,
}: {
  productId: number;
  initialStock: ProductStock[];
}) {
  // El padre (ProductDetailPage) ya llama useProductStockByWarehouse(productId)
  // y nos pasa los datos como initialStock. PERO queremos datos frescos
  // cada vez que se monta este tab, asi que llamamos la query nosotros
  // mismos para tener cache propio e invalidacion granular.
  const { data: freshStock = [], isLoading } = useProductStockByWarehouse(productId);
  // Preferimos freshStock si tiene datos (puede ser array vacio si
  // el backend retorno vacio de verdad); fallback a initialStock
  // para evitar parpadeo durante el refetch.
  const stock = freshStock.length > 0 || isLoading ? freshStock : initialStock;

  if (isLoading && initialStock.length === 0) {
    return <Spinner label="Cargando stock..." />;
  }

  if (stock.length === 0) {
    return (
      <EmptyState
        title="Sin stock registrado"
        description="Este producto aún no tiene stock en ningún almacén."
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Stock por almacén</CardTitle>
        <CardDescription>Distribucion actual del producto.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full table-dense">
          <thead className="border-b border-border bg-bg/60 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">
                Almacén
              </th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-text-secondary">
                Disponible
              </th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-text-secondary">
                Reservado
              </th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-text-secondary">
                Dañado
              </th>
            </tr>
          </thead>
<tbody>
            {stock.map((s) => {
              // IMPORTANTE: el endpoint /inventory-center/products/{id}/stock-by-warehouse
              // retorna los campos como 'available', 'reserved', 'damaged'
              // (NO 'quantity'). Auditado leyendo
              // InventoryCenterProductDetailService::stockByWarehouse()
              // (linea ~280). Usamos esos nombres y un fallback defensivo
              // para evitar "0.00" cuando el shape cambia.
              const parseNum = (v: unknown): number => {
                if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
                if (typeof v === 'string') {
                  const n = parseFloat(v);
                  return Number.isFinite(n) ? n : 0;
                }
                return 0;
              };
              const qty = parseNum(s.available);
              const res = parseNum(s.reserved ?? 0);
              const dmg = parseNum(s.damaged ?? 0);
              return (
                <tr key={s.warehouse_id} className="border-b border-border last:border-b-0">
                  <td className="px-3 py-2">
                    <div className="font-medium">{s.warehouse_name}</div>
                    <div className="text-xs text-text-muted">{s.warehouse_code}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-text-primary font-medium">
                    {qty.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-text-muted">
                    {res.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-text-muted">
                    {dmg.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function SerialsTab({
  productId,
  initialSerials,
}: {
  productId: number;
  initialSerials: ProductSerial[];
}) {
  const { data: serialList = [], isLoading } = useProductSerials(productId);
  const serials: ProductSerial[] = serialList.length > 0 ? serialList : initialSerials;
  if (isLoading) return <Spinner label="Cargando seriales..." />;
  if (serials.length === 0) {
    return (
      <EmptyState
        title="Sin seriales registrados"
        description="Este producto serializado aún no tiene IMEI/serial asignados."
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Seriales / IMEI</CardTitle>
        <CardDescription>{serials.length} unidades físicas.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full table-dense">
          <thead className="border-b border-border bg-bg/60 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Serial</th>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Tipo</th>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Estado</th>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Almacén</th>
            </tr>
          </thead>
          <tbody>
            {serials.map((s: ProductSerial) => (
              <tr key={s.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2 font-mono text-xs">{s.serial_number}</td>
                <td className="px-3 py-2 text-text-muted">{s.serial_type}</td>
                <td className="px-3 py-2">
                  <Badge
                    variant={
                      s.status === 'available'
                        ? 'success'
                        : s.status === 'sold'
                          ? 'default'
                          : s.status === 'damaged'
                            ? 'danger'
                            : 'warning'
                    }
                  >
                    {s.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-text-muted">{s.warehouse_name ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PricesTab({ productId }: { productId: number }) {
  return <PricesEditor productId={productId} />;
}

function MovementsTab({ movements }: { movements: ProductMovement[] }) {
  if (movements.length === 0) {
    return (
      <EmptyState
        title="Sin movimientos"
        description="Este producto aún no tiene movimientos de stock."
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Movimientos recientes</CardTitle>
        <CardDescription>Últimos {movements.length} movimientos de stock.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full table-dense">
          <thead className="border-b border-border bg-bg/60 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Fecha</th>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Tipo</th>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Almacén</th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-text-secondary">Cantidad</th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-text-secondary">Costo unit.</th>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Referencia</th>
              <th className="px-3 py-2 font-semibold uppercase tracking-wide text-text-secondary">Usuario</th>
            </tr>
          </thead>
          <tbody>
            {movements.map((m) => (
              <tr key={m.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2 text-text-muted">{formatRelative(m.created_at)}</td>
                <td className="px-3 py-2">
                  <Badge
                    variant={MOVEMENT_IN_TYPES.has(m.type) ? 'success' : 'warning'}
                    data-testid={`movement-type-${m.id}`}
                  >
                    {movementTypeLabel(m.type)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-text-muted">{m.warehouse_name ?? '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{m.quantity}</td>
                <td className={cn('px-3 py-2 text-right tabular-nums', m.unit_cost == null && 'text-text-muted')}>
                  {formatCost(m.unit_cost)}
                </td>
                <td className="px-3 py-2 text-xs text-text-muted">
                  {(() => {
                    const link = referenceLink(m.reference_type, m.reference_id);
                    if (link) {
                      return (
                        <Link
                          to={link.to}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                          data-testid={`movement-ref-${m.id}`}
                        >
                          {link.label} #{m.reference_id}
                          <ExternalLink className="size-3" />
                        </Link>
                      );
                    }
                    return (
                      <span>
                        {referenceTypeLabel(m.reference_type)}
                        {m.reference_id != null ? ` #${m.reference_id}` : ''}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 text-text-muted">{m.user_name ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

